import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import usePoll from "./usePoll";
import { discussionApi } from "../services/api";

// Something may have changed what's for you (a thread was marked read).
export const DISCUSSIONS_CHANGED = "mono:discussions-changed";

/** Say so: the nav's count asks again at once. */
export function discussionsChanged() {
  window.dispatchEvent(new Event(DISCUSSIONS_CHANGED));
}

const POLL_MS = 60000;

/**
 * How many discussions are for you — the nav's badge (projects/discussions
 * D14): the same set as the list's For you. Asks when it starts, every
 * minute while the tab is visible, at once when you move between
 * discussions pages, and whenever `discussionsChanged()` says a thread was
 * read — so the badge drops as soon as you've read the mention.
 *
 * Returns the count, or null while unknown (or disabled: signed out). A
 * failed ask keeps the last count and the next minute tries again (the
 * poll's backoff caps at the same minute, so it never slows further).
 */
export default function useForYouCount({ enabled = true } = {}) {
  const { pathname } = useLocation();
  const [count, setCount] = useState(null);
  // The newest ask wins: an older answer can't land on a newer one.
  const request = useRef(0);

  // (The counter also covers usePoll's stale(): a restarted loop asks
  // again at once, which makes any older answer stale here.)
  const ask = useCallback(async () => {
    const id = ++request.current;
    const { data } = await discussionApi.forYou();
    if (id !== request.current) return;
    setCount(data.count);
  }, []);

  // Moving between discussions pages restarts the loop with an ask;
  // moving around anywhere else doesn't.
  const where = pathname.startsWith("/discussions") ? pathname : "elsewhere";
  usePoll(ask, {
    intervalMs: POLL_MS,
    enabled,
    immediate: true,
    resetKey: where,
  });

  // A burst of "read" events (each thread reload sends one) asks once, then
  // once more if any came in meanwhile — never one request per event.
  useEffect(() => {
    if (!enabled) return undefined;
    let running = false;
    let again = false;
    let stopped = false;
    const run = async () => {
      running = true;
      try {
        await ask();
      } catch {
        // the next event or the minute's poll tries again
      } finally {
        running = false;
      }
      if (again && !stopped) {
        again = false;
        run();
      }
    };
    const onChange = () => {
      if (running) again = true;
      else run();
    };
    window.addEventListener(DISCUSSIONS_CHANGED, onChange);
    return () => {
      stopped = true;
      window.removeEventListener(DISCUSSIONS_CHANGED, onChange);
    };
  }, [enabled, ask]);

  // Signed out (or not yet known): no badge, and nothing in flight lands.
  useEffect(() => {
    if (enabled) return;
    request.current += 1;
    setCount(null);
  }, [enabled]);

  return count;
}
